import type {
  ApplePayPaymentRequest,
  ApplePayValidateMerchantEvent,
} from './applePayTypes';
import { apiClient, getCurrentEnvironment } from '@kuow/core';
import { logger } from '@kuow/core';

interface ApplePaySessionClass {
  canMakePaymentsWithActiveCard: (merchantIdentifier: string) => Promise<boolean>;
  STATUS_SUCCESS: number;
  STATUS_FAILURE: number;
  new (version: number, request: ApplePayPaymentRequest): unknown;
}

function isApplePaySessionClass(value: unknown): value is ApplePaySessionClass {
  if (typeof value !== 'function') {
    return false;
  }

  const record = value as unknown as Record<string, unknown>;
  return (
    typeof record.canMakePaymentsWithActiveCard === 'function' &&
    typeof record.STATUS_SUCCESS === 'number' &&
    typeof record.STATUS_FAILURE === 'number'
  );
}

function getApplePaySessionClass(): ApplePaySessionClass | null {
  const maybeApplePaySession = (window as unknown as { ApplePaySession?: unknown }).ApplePaySession;
  return isApplePaySessionClass(maybeApplePaySession) ? maybeApplePaySession : null;
}

// Global Apple Pay session reference
// ApplePaySession is a browser global type - using any for compatibility
// eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-redundant-type-constituents
let applePaySession: any = null;

/**
 * Check if device can make Apple Pay payments
 */
export async function canMakeApplePayPayments(): Promise<boolean> {
  logger.info('Checking Apple Pay support');

  const ApplePaySessionClass = getApplePaySessionClass();

  if (!ApplePaySessionClass) {
    logger.info('Apple Pay not supported on this device');

    return false;
  }

  try {
    const environment = getCurrentEnvironment();
    const isProduction = environment === 'production';
    const merchantEnvironment = isProduction ? 'prod' : 'sandbox';
    const merchantId = `merchant.org.kuow.${merchantEnvironment}`;

    const canMakePayments = await ApplePaySessionClass.canMakePaymentsWithActiveCard(merchantId);
    logger.info('Apple Pay capability check', { canMakePayments });

    return canMakePayments;
  } catch (error) {
    logger.error('Error checking Apple Pay capability', { error });

    return false;
  }
}

interface DonationData {
  amount: number;
  amountWithCoverCost: number;
  monthly: boolean;
  coverCost: boolean;
  coverCostAmount: number;
  shippingRequired: boolean;
}

/**
 * Prepare Apple Pay payment request
 */
export function prepareApplePayPaymentRequest(
  donationData: DonationData
): ApplePayPaymentRequest {
  const amount = donationData.amountWithCoverCost.toFixed(2);

  const request: ApplePayPaymentRequest = {
    merchantCapabilities: ['supports3DS'],
    supportedNetworks: ['amex', 'discover', 'masterCard', 'visa'],
    countryCode: 'US',
    currencyCode: 'USD',
    requiredBillingContactFields: ['name', 'email', 'postalAddress'],
    requiredShippingContactFields: ['email'],
    lineItems: [
      {
        label: 'Donation',
        type: 'final',
        amount,
        recurringPaymentIntervalUnit: donationData.monthly ? 'month' : undefined,
        paymentTiming: donationData.monthly ? 'recurring' : 'immediate',
      },
    ],
    total: {
      label: 'KUOW News & Information',
      type: 'final',
      amount,
      recurringPaymentIntervalUnit: donationData.monthly ? 'month' : undefined,
      paymentTiming: donationData.monthly ? 'recurring' : 'immediate',
    },
  };

  // If shipping is required, add shipping address fields
  if (donationData.shippingRequired) {
    request.requiredShippingContactFields = ['name', 'email', 'postalAddress'];
  }

  // If cover cost is enabled, show line items breakdown
  if (donationData.coverCost) {
    request.lineItems = [
      {
        label: 'Donation',
        type: 'final',
        amount: donationData.amount.toFixed(2),
        recurringPaymentIntervalUnit: donationData.monthly ? 'month' : undefined,
        paymentTiming: donationData.monthly ? 'recurring' : 'immediate',
      },
      {
        label: 'Cover Cost',
        type: 'final',
        amount: donationData.coverCostAmount.toFixed(2),
        recurringPaymentIntervalUnit: donationData.monthly ? 'month' : undefined,
        paymentTiming: donationData.monthly ? 'recurring' : 'immediate',
      },
    ];
  }

  return request;
}

/**
 * Start Apple Pay session
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function startApplePaySession(
  request: ApplePayPaymentRequest
// eslint-disable-next-line @typescript-eslint/no-explicit-any
): Promise<any> {
  return new Promise((resolve, reject) => {
    const ApplePaySessionClass = getApplePaySessionClass();

    if (!ApplePaySessionClass) {
      reject(new Error('Apple Pay not supported'));
      return;
    }

    try {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      applePaySession = new ApplePaySessionClass(14, request);

      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      applePaySession.onvalidatemerchant = async (event: ApplePayValidateMerchantEvent) => {
        try {
          const validationURL = event.validationURL;
          logger.info('Validating Apple Pay merchant', { validationURL });

          // this unknown type is unavoidable
          const response = await apiClient.post<unknown>(
            '/donate/v1/payments/apple-pay/session',
            { url: validationURL }
          );

          if (response && applePaySession) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
            applePaySession.completeMerchantValidation(response);
            resolve(applePaySession);
          } else {
            reject(new Error('Apple Pay session was cleared'));
          }
        } catch (error) {
          logger.error('Apple Pay merchant validation failed', { error });
          reject(error instanceof Error ? error : new Error('Apple Pay merchant validation failed'));
          applePaySession = null;
        }
      };

      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
      applePaySession.begin();
    } catch (error) {
      logger.error('Failed to start Apple Pay session', { error });
      reject(error instanceof Error ? error : new Error('Failed to start Apple Pay session'));
      applePaySession = null;
    }
  });
}

/**
 * Complete Apple Pay session with success or failure status
 */
export function completeApplePaySession(success: boolean): void {
  const ApplePaySessionClass = getApplePaySessionClass();

  if (applePaySession && ApplePaySessionClass) {
    logger.info('Completing Apple Pay session', { success });

    const status = success
      ? ApplePaySessionClass.STATUS_SUCCESS
      : ApplePaySessionClass.STATUS_FAILURE;

    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
    applePaySession.completePayment(status);
    applePaySession = null;
  }
}
